import { Server, CheckSquare, Square } from 'lucide-react'
import type { CaseEndpoint } from '../../../types/case-container'
import { CaseInfrastructureTableRow } from './CaseInfrastructureTable.row'

const TH =
  'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400'
const TH_CENTER =
  'px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400'
const TH_RIGHT =
  'px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400'

export interface CaseInfrastructureTableProps {
  endpoints: CaseEndpoint[]
  visibleColumns: Set<string>
  isSelected: (id: string) => boolean
  isInSet: (id: string) => boolean
  isInCollectionSet: (id: string) => boolean
  allVisibleSelected: boolean
  someVisibleSelected: boolean
  deleting: boolean
  bulkDeleting: boolean
  handleSelectAllVisible: () => void
  handleCheckboxClick: (endpointId: string, e: React.MouseEvent) => void
  handleLinkButtonContextMenu: (e: React.MouseEvent, endpoint: CaseEndpoint) => void
  handleMapButtonContextMenu: (e: React.MouseEvent, endpoint: CaseEndpoint) => void
  handleCollectionButtonContextMenu: (e: React.MouseEvent, endpoint: CaseEndpoint) => void
  handleDeleteContextMenu: (e: React.MouseEvent, endpoint: CaseEndpoint) => void
  handleDeleteSingle: (endpoint: CaseEndpoint) => void
  toggleMapSet: (id: string) => void
  toggleCollectionSet: (id: string) => void
  setLinkingEndpoint: (e: CaseEndpoint) => void
  setEditingEndpoint: (e: CaseEndpoint) => void
  setShowCreateModal: (v: boolean) => void
  getRelationshipCount: (id: string) => number
}

const COLUMN_HEADERS: Array<{ key: string; label: string; cls: string }> = [
  { key: 'hostname', label: 'Hostname', cls: TH },
  { key: 'type', label: 'Type', cls: TH },
  { key: 'ipAddresses', label: 'IP Addresses', cls: TH },
  { key: 'domain', label: 'Domain', cls: TH },
  { key: 'os', label: 'OS', cls: TH },
  { key: 'osVersion', label: 'OS Version', cls: TH },
  { key: 'ouPath', label: 'OU Path', cls: TH },
  { key: 'importance', label: 'Importance', cls: TH_CENTER },
  { key: 'roles', label: 'Roles', cls: TH },
  { key: 'links', label: 'Links', cls: TH_CENTER },
]

function SelectAllButton({
  allVisibleSelected,
  someVisibleSelected,
  onSelectAll,
}: {
  allVisibleSelected: boolean
  someVisibleSelected: boolean
  onSelectAll: () => void
}): React.ReactElement {
  return (
    <button
      onClick={onSelectAll}
      className="flex h-5 w-5 items-center justify-center rounded border border-slate-300 transition-colors hover:border-[#4e5e82] dark:border-slate-600 dark:hover:border-[#4e5e82]"
      title={allVisibleSelected ? 'Deselect all visible' : 'Select all visible'}
    >
      {allVisibleSelected ? (
        <CheckSquare className="h-4 w-4 text-lime-600 dark:text-lime-400" />
      ) : someVisibleSelected ? (
        <div className="h-2.5 w-2.5 rounded-sm bg-lime-500" />
      ) : (
        <Square className="h-4 w-4 text-slate-400" />
      )}
    </button>
  )
}

function TableHeader({
  visibleColumns,
  allVisibleSelected,
  someVisibleSelected,
  onSelectAll,
}: {
  visibleColumns: Set<string>
  allVisibleSelected: boolean
  someVisibleSelected: boolean
  onSelectAll: () => void
}): React.ReactElement {
  return (
    <thead className="bg-slate-50 dark:bg-slate-800/50">
      <tr>
        <th className="w-10 px-3 py-3">
          <SelectAllButton
            allVisibleSelected={allVisibleSelected}
            someVisibleSelected={someVisibleSelected}
            onSelectAll={onSelectAll}
          />
        </th>
        {COLUMN_HEADERS.filter((col) => visibleColumns.has(col.key)).map((col) => (
          <th key={col.key} className={col.cls}>
            {col.label}
          </th>
        ))}
        <th className={TH_RIGHT}>Actions</th>
      </tr>
    </thead>
  )
}

function CaseInfrastructureEmptyState({ onAdd }: { onAdd: () => void }): React.ReactElement {
  return (
    <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center dark:border-slate-700">
      <Server className="mx-auto h-12 w-12 text-slate-300 dark:text-slate-600" />
      <p className="mt-4 text-slate-600 dark:text-slate-400">No endpoints found</p>
      <button
        onClick={onAdd}
        className="mt-4 text-sm text-lime-600 hover:underline dark:text-lime-400"
      >
        Add your first endpoint
      </button>
    </div>
  )
}

export function CaseInfrastructureTable(props: CaseInfrastructureTableProps): React.ReactElement {
  const {
    endpoints,
    visibleColumns,
    isSelected,
    isInSet,
    isInCollectionSet,
    allVisibleSelected,
    someVisibleSelected,
    deleting,
    bulkDeleting,
    handleSelectAllVisible,
    handleCheckboxClick,
    handleLinkButtonContextMenu,
    handleMapButtonContextMenu,
    handleCollectionButtonContextMenu,
    handleDeleteContextMenu,
    handleDeleteSingle,
    toggleMapSet,
    toggleCollectionSet,
    setLinkingEndpoint,
    setEditingEndpoint,
    setShowCreateModal,
    getRelationshipCount,
  } = props

  if (endpoints.length === 0) {
    return (
      <CaseInfrastructureEmptyState
        onAdd={() => {
          setShowCreateModal(true)
        }}
      />
    )
  }

  return (
    <div className="overflow-hidden overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
      <table className="w-full">
        <TableHeader
          visibleColumns={visibleColumns}
          allVisibleSelected={allVisibleSelected}
          someVisibleSelected={someVisibleSelected}
          onSelectAll={handleSelectAllVisible}
        />
        <tbody className="divide-y divide-slate-200 bg-white dark:divide-slate-700 dark:bg-slate-900">
          {endpoints.map((endpoint) => (
            <CaseInfrastructureTableRow
              key={endpoint.id}
              endpoint={endpoint}
              visibleColumns={visibleColumns}
              isSelected={isSelected(endpoint.id)}
              isInMapSet={isInSet(endpoint.id)}
              isInCollectionSet={isInCollectionSet(endpoint.id)}
              relationshipCount={getRelationshipCount(endpoint.id)}
              deleting={deleting}
              bulkDeleting={bulkDeleting}
              handleCheckboxClick={handleCheckboxClick}
              handleLinkButtonContextMenu={handleLinkButtonContextMenu}
              handleMapButtonContextMenu={handleMapButtonContextMenu}
              handleCollectionButtonContextMenu={handleCollectionButtonContextMenu}
              handleDeleteContextMenu={handleDeleteContextMenu}
              handleDeleteSingle={handleDeleteSingle}
              toggleMapSet={toggleMapSet}
              toggleCollectionSet={toggleCollectionSet}
              setLinkingEndpoint={setLinkingEndpoint}
              setEditingEndpoint={setEditingEndpoint}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}
